import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns'
import { pt } from 'date-fns/locale'
import {
  getFaturas,
  getConfiguracoes,
  getTamanhoPaginacao,
  cancelarFaturaAgendamento,
  descarregarFaturaPdf,
} from '../../lib/supabase'
import { fmtEur, labelMetodo, parseMetodosPagamento, METODO_STRIPE } from '../../lib/valores'
import {
  ESTADOS_FATURA, ORDEM_ESTADOS_FATURA, estadoFaturaCfg,
  itensVisiveis as linhasVisiveis, faturaVisivel, contarMarcacoes,
} from '../../lib/faturas'
import { descarregarXlsx } from '../../lib/excel'
import { useTenant } from '../../contexts/TenantContext'
import { useToast } from '../../contexts/ToastContext'
import { useAuth } from '../../contexts/AuthContext'
import { ESTADOS, ORDEM_ESTADOS, estadoCfg } from '../../lib/estados'
import Loading from '../../components/Loading'

const fmtData = (d) => format(d, 'yyyy-MM-dd')
const TIPO_DOC = { FS: 'Fatura simplificada', FR: 'Fatura-recibo' }

export default function Faturacao() {
  const navigate = useNavigate()
  const { tenant } = useTenant()
  const toast = useToast()
  const { perfil, podeFazer, estadosVisiveisFaturacao } = useAuth()
  const hoje = new Date()
  const perfilNome = perfil?.nome || perfil?.email || 'backoffice'

  const [inicio, setInicio] = useState(fmtData(startOfMonth(hoje)))
  const [fim, setFim] = useState(fmtData(hoje))
  const [faturas, setFaturas] = useState([])
  const [carregando, setCarregando] = useState(false)
  const [tamanhoPagina, setTamanhoPagina] = useState(20)
  const [pagina, setPagina] = useState(1)

  // Filtros
  const [tipoDoc, setTipoDoc] = useState('todos')      // todos | FS | FR
  const [estadoDoc, setEstadoDoc] = useState('todos')  // todos | emitida | cancelada | anulada_vendus
  const [texto, setTexto] = useState('')
  const [estadoSel, setEstadoSel] = useState({})
  const [metodoSel, setMetodoSel] = useState({})
  const [comboAberta, setComboAberta] = useState(false)

  // Detalhe + cancelamento
  const [faturaAberta, setFaturaAberta] = useState(null)
  const [aConfirmar, setAConfirmar] = useState(false)
  const [cancelando, setCancelando] = useState(false)
  const [reabrirPagamentos, setReabrirPagamentos] = useState(false)
  const [obtendoPdf, setObtendoPdf] = useState(false)

  const comboRef = useRef(null)

  // Estados que este perfil pode ver, na ordem canónica.
  const estadosPermitidos = useMemo(
    () => ORDEM_ESTADOS.filter(e => estadosVisiveisFaturacao.has(e)),
    [estadosVisiveisFaturacao]
  )

  // Meios de pagamento do tenant (Config Admin → "Tipos de pagamento"), mais o
  // sinal via Stripe, que não é um método escolhível no salão mas aparece nos
  // documentos emitidos a partir do sinal online.
  const [metodosTenant, setMetodosTenant] = useState([])
  const metodos = useMemo(
    () => [...metodosTenant, { chave: METODO_STRIPE, label: 'Stripe (online)' }],
    [metodosTenant]
  )

  useEffect(() => {
    if (!tenant) return
    getConfiguracoes(tenant.id)
      .then(cfg => setMetodosTenant(parseMetodosPagamento(cfg)))
      .catch(() => setMetodosTenant(parseMetodosPagamento(null)))
    getTamanhoPaginacao(tenant.id).then(setTamanhoPagina).catch(() => {})
  }, [tenant])

  // Filtros de estado: liga os permitidos, larga os que deixaram de o ser.
  useEffect(() => {
    setEstadoSel(prev => {
      const next = {}
      for (const e of estadosPermitidos) next[e] = e in prev ? prev[e] : true
      return next
    })
  }, [estadosPermitidos])

  // Métodos: todos ligados por defeito.
  useEffect(() => {
    setMetodoSel(prev => {
      const next = {}
      for (const m of metodos) next[m.chave] = m.chave in prev ? prev[m.chave] : true
      return next
    })
  }, [metodos])

  useEffect(() => {
    if (!tenant) return
    setCarregando(true)
    const inicioISO = new Date(inicio + 'T00:00:00').toISOString()
    const fimISO = new Date(fim + 'T23:59:59').toISOString()
    getFaturas(tenant.id, inicioISO, fimISO)
      .then(setFaturas)
      .catch(e => toast.erro(e.message))
      .finally(() => setCarregando(false))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant, inicio, fim])

  // Fechar a combobox ao clicar fora.
  useEffect(() => {
    if (!comboAberta) return
    const fora = (e) => { if (comboRef.current && !comboRef.current.contains(e.target)) setComboAberta(false) }
    document.addEventListener('mousedown', fora)
    return () => document.removeEventListener('mousedown', fora)
  }, [comboAberta])

  // Fechar o painel de detalhe com Escape.
  useEffect(() => {
    if (!faturaAberta) return
    const tecla = (e) => { if (e.key === 'Escape') fecharPainel() }
    document.addEventListener('keydown', tecla)
    return () => document.removeEventListener('keydown', tecla)
  }, [faturaAberta])

  function presetHoje() { const d = new Date(); setInicio(fmtData(d)); setFim(fmtData(d)) }
  function presetSemana() {
    const d = new Date()
    setInicio(fmtData(startOfWeek(d, { weekStartsOn: 1 })))
    setFim(fmtData(endOfWeek(d, { weekStartsOn: 1 })))
  }
  function presetMes() {
    const d = new Date()
    setInicio(fmtData(startOfMonth(d)))
    setFim(fmtData(endOfMonth(d)))
  }

  function fecharPainel() { setFaturaAberta(null); setAConfirmar(false); setReabrirPagamentos(false) }

  // Linhas do documento visíveis a este perfil. O filtro de estados é da vista;
  // dentro do documento aberto vale só a permissão.
  const itensVisiveis = (f) => linhasVisiveis(f, estadosVisiveisFaturacao)

  const estadosAtivos = useMemo(() => {
    const s = new Set()
    for (const e of estadosPermitidos) if (estadoSel[e]) s.add(e)
    return s
  }, [estadosPermitidos, estadoSel])

  const semMetodos = useMemo(() => metodos.length > 0 && !metodos.some(m => metodoSel[m.chave]), [metodos, metodoSel])

  // Filtragem principal.
  const filtradas = useMemo(() => {
    const t = texto.trim().toLowerCase()
    return faturas.filter(f => {
      if (tipoDoc !== 'todos' && f.tipo !== tipoDoc) return false
      if (estadoDoc !== 'todos' && f.estado !== estadoDoc) return false
      if (!metodoSel[f.metodo || 'outro']) return false
      // Só aparece se alguma marcação do documento estiver num estado visível.
      if (!faturaVisivel(f, estadosAtivos)) return false
      if (t && !String(f.id).toLowerCase().includes(t) && !String(f.cliente || '').toLowerCase().includes(t)) return false
      return true
    })
  }, [faturas, tipoDoc, estadoDoc, metodoSel, estadosAtivos, texto])

  useEffect(() => { setPagina(1) }, [filtradas, tamanhoPagina])

  const totalPaginas = Math.max(1, Math.ceil(filtradas.length / tamanhoPagina))
  const paginaAtual = Math.min(pagina, totalPaginas)
  const paginaLinhas = useMemo(() => {
    const i = (paginaAtual - 1) * tamanhoPagina
    return filtradas.slice(i, i + tamanhoPagina)
  }, [filtradas, paginaAtual, tamanhoPagina])

  // Nº de documentos por método, ignorando o próprio filtro de métodos.
  const contagemMetodos = useMemo(() => {
    const t = texto.trim().toLowerCase()
    const m = {}
    for (const x of metodos) m[x.chave] = 0
    for (const f of faturas) {
      if (tipoDoc !== 'todos' && f.tipo !== tipoDoc) continue
      if (estadoDoc !== 'todos' && f.estado !== estadoDoc) continue
      if (!faturaVisivel(f, estadosAtivos)) continue
      if (t && !String(f.id).toLowerCase().includes(t) && !String(f.cliente || '').toLowerCase().includes(t)) continue
      const k = f.metodo || 'outro'
      if (m[k] != null) m[k] += 1
    }
    return m
  }, [faturas, metodos, tipoDoc, estadoDoc, estadosAtivos, texto])

  const resumo = useMemo(() => {
    const emitidas = filtradas.filter(f => f.estado === 'emitida')
    const canceladas = filtradas.filter(f => f.estado !== 'emitida')
    return {
      nEmitidas: emitidas.length,
      nFS: emitidas.filter(f => f.tipo === 'FS').length,
      nFR: emitidas.filter(f => f.tipo === 'FR').length,
      faturado: emitidas.reduce((s, f) => s + f.total, 0),
      nCanceladas: canceladas.length,
      anulado: canceladas.reduce((s, f) => s + f.total, 0),
      nLinhas: filtradas.reduce((s, f) => s + itensVisiveis(f).length, 0),
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtradas, estadosVisiveisFaturacao])

  async function obterPdf(faturaId) {
    setObtendoPdf(true)
    try {
      const { url } = await descarregarFaturaPdf(tenant.id, { faturaId })
      window.open(url, '_blank', 'noopener')
    } catch (e) {
      toast.erro(e.message)
    } finally {
      setObtendoPdf(false)
    }
  }

  // Cancela o documento inteiro (a RPC limpa todas as parcelas com o mesmo
  // fatura_id). Basta indicar uma das marcações abrangidas.
  async function cancelarFatura() {
    const f = faturaAberta
    const alguma = (f.itens || []).find(i => i.agendamento_id)
    if (!alguma) { toast.erro('Esta fatura não tem marcações associadas.'); return }
    setCancelando(true)
    try {
      const r = await cancelarFaturaAgendamento(tenant.id, alguma.agendamento_id, perfilNome, reabrirPagamentos)
      const n = r?.n_marcacoes || contarMarcacoes(f.itens)
      const nReab = r?.pagamentos_reabertos || 0
      const nStripe = r?.stripe_mantidos || 0
      toast.sucesso(
        `Fatura cancelada no sistema${n > 1 ? ` (${n} marcações do documento)` : ''}. ` +
        (nReab > 0 ? `${nReab} pagamento${nReab === 1 ? '' : 's'} reaberto${nReab === 1 ? '' : 's'} — a marcação volta a aparecer como por cobrar. ` : '') +
        (nStripe > 0 ? `${nStripe} pagamento${nStripe === 1 ? '' : 's'} por Stripe manteve-se pago (esses nunca reabrem). ` : '') +
        `Anule também o documento ${r?.fatura_id || f.id} no Vendus.`
      )
      fecharPainel()
      setReabrirPagamentos(false)
      const inicioISO = new Date(inicio + 'T00:00:00').toISOString()
      const fimISO = new Date(fim + 'T23:59:59').toISOString()
      setFaturas(await getFaturas(tenant.id, inicioISO, fimISO))
    } catch (e) {
      toast.erro(e.message)
    } finally {
      setCancelando(false)
    }
  }

  // Exportação: uma linha por parcela de cada marcação do documento. Leva os
  // filtros do ecrã e só os estados que o perfil pode ver.
  function exportar() {
    const cols = [
      { t: 'Documento', w: 16 }, { t: 'Tipo', w: 6 }, { t: 'Tipo (descrição)', w: 19 },
      { t: 'Data de emissão', w: 17 }, { t: 'Estado do documento', w: 19 }, { t: 'Cancelada em', w: 13 },
      { t: 'Cliente', w: 22 }, { t: 'Método', w: 16 }, { t: 'Cobrança agrupada', w: 17 },
      { t: 'Marcação', w: 17 }, { t: 'Serviço', w: 32 }, { t: 'Profissional', w: 18 },
      { t: 'Estado da marcação', w: 18 }, { t: 'Parcela', w: 10 },
      { t: 'Valor da linha', w: 14 }, { t: 'Total do documento', w: 18 },
      { t: 'Link', w: 46 },
    ]
    const linhas = []
    for (const f of filtradas) {
      for (const i of itensVisiveis(f)) {
        linhas.push([
          f.id, f.tipo || '', TIPO_DOC[f.tipo] || '',
          { d: f.data }, estadoFaturaCfg(f.estado).label,
          f.cancelada_em ? { d: f.cancelada_em, dia: true } : '',
          f.cliente || '', labelMetodo(f.metodo, metodosTenant), f.lote ? 'Sim' : 'Não',
          { d: i.quando }, i.servico || '', i.profissional || '',
          i.estado ? estadoCfg(i.estado).label : '', i.parcela === 'sinal' ? 'Sinal' : 'Restante',
          { n: i.valor }, { n: f.total },
          i.agendamento_id ? `/admin/agendamento/${i.agendamento_id}` : '',
        ])
      }
    }
    if (linhas.length === 0) { toast.erro('Não há linhas para exportar com estes filtros.'); return }
    descarregarXlsx(`faturacao_${inicio}_a_${fim}.xlsx`, [{ nome: 'Faturas (linhas)', cols, linhas }])
    toast.sucesso(`Exportadas ${linhas.length} linhas de fatura.`)
  }

  const etiquetaMetodos = (() => {
    const escolhidos = metodos.filter(m => metodoSel[m.chave])
    if (escolhidos.length === metodos.length) return 'Todos os métodos'
    if (escolhidos.length === 0) return 'Nenhum método'
    if (escolhidos.length <= 2) return escolhidos.map(m => m.label).join(', ')
    return `${escolhidos.length} métodos`
  })()

  const periodoLabel = inicio === fim
    ? format(new Date(inicio + 'T12:00:00'), "d 'de' MMMM 'de' yyyy", { locale: pt })
    : `${format(new Date(inicio + 'T12:00:00'), 'd MMM yyyy', { locale: pt })} — ${format(new Date(fim + 'T12:00:00'), 'd MMM yyyy', { locale: pt })}`

  return (
    <div style={{ maxWidth: '980px', margin: '0 auto', padding: '32px 24px 64px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', marginBottom: '4px' }}>
        <h2 style={{ fontSize: '22px' }}>Faturação</h2>
        <button onClick={exportar} className="btn-primario" style={{ padding: '9px 16px', fontSize: '14px' }}>
          <span aria-hidden="true">⬇️</span> Exportar Excel
        </button>
      </div>
      <p style={{ color: 'var(--cor-texto-suave)', fontSize: '13px', marginBottom: '20px' }}>
        Documentos emitidos no Vendus a partir das marcações · {periodoLabel}
      </p>

      {/* Controlos */}
      <div style={{ background: 'var(--cor-branco)', border: '1px solid var(--cor-borda)', borderRadius: 'var(--raio-lg)', padding: '18px 20px', marginBottom: '22px', boxShadow: 'var(--sombra)' }}>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' }}>
          <button onClick={presetHoje} className="btn-secundario" style={btnPreset}>Hoje</button>
          <button onClick={presetSemana} className="btn-secundario" style={btnPreset}>Esta semana</button>
          <button onClick={presetMes} className="btn-secundario" style={btnPreset}>Este mês</button>
        </div>

        <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '16px' }}>
          <div>
            <label style={rotulo}>De</label>
            <input type="date" value={inicio} max={fim} onChange={e => setInicio(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={rotulo}>Até</label>
            <input type="date" value={fim} min={inicio} onChange={e => setFim(e.target.value)} style={inputStyle} />
          </div>

          <div>
            <label style={rotulo}>Tipo de documento</label>
            <div style={segmentado}>
              {[['todos', 'Todos'], ['FS', 'FS'], ['FR', 'FR']].map(([v, l], i) => (
                <button key={v} onClick={() => setTipoDoc(v)} style={segBotao(tipoDoc === v, i > 0)}>{l}</button>
              ))}
            </div>
          </div>

          {/* Meios de pagamento — multiselect */}
          <div ref={comboRef} style={{ position: 'relative' }}>
            <label style={rotulo}>Meios de pagamento</label>
            <button
              type="button"
              onClick={() => setComboAberta(a => !a)}
              aria-expanded={comboAberta}
              aria-haspopup="listbox"
              style={{ ...inputStyle, minWidth: '200px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', cursor: 'pointer', textAlign: 'left' }}
            >
              <span>{etiquetaMetodos}</span>
              <span aria-hidden="true" style={{ color: 'var(--cor-texto-suave)', fontSize: '11px' }}>▾</span>
            </button>
            {comboAberta && (
              <div role="listbox" aria-multiselectable="true" style={caixaCombo}>
                {metodos.map(m => (
                  <label key={m.chave} style={opcaoCombo}>
                    <input
                      type="checkbox"
                      checked={!!metodoSel[m.chave]}
                      onChange={() => setMetodoSel(p => ({ ...p, [m.chave]: !p[m.chave] }))}
                      style={{ width: '15px', height: '15px', accentColor: 'var(--cor-principal)' }}
                    />
                    <span>{m.label}</span>
                    <span style={{ marginLeft: 'auto', paddingLeft: '14px', fontSize: '11.5px', color: 'var(--cor-texto-suave)' }}>
                      {contagemMetodos[m.chave] || 0}
                    </span>
                  </label>
                ))}
                <div style={{ display: 'flex', gap: '6px', paddingTop: '7px', marginTop: '5px', borderTop: '1px solid var(--cor-borda)' }}>
                  <button onClick={() => setMetodoSel(Object.fromEntries(metodos.map(m => [m.chave, true])))} className="btn-secundario" style={btnMini}>Todos</button>
                  <button onClick={() => setMetodoSel(Object.fromEntries(metodos.map(m => [m.chave, false])))} className="btn-secundario" style={btnMini}>Limpar</button>
                </div>
              </div>
            )}
          </div>

          <div>
            <label style={rotulo}>Estado do documento</label>
            <div style={segmentado}>
              {/* "Anulada no Vendus" fica fora do filtro: não há ainda nenhuma
                  ação na interface que ponha uma fatura nesse estado (falta a
                  fase 2 — nota de crédito automática), por isso filtrar por
                  ele nunca devolveria nada. */}
              {[['todos', 'Todas'], ...ORDEM_ESTADOS_FATURA.filter(e => e !== 'anulada_vendus').map(e => [e, ESTADOS_FATURA[e].label])].map(([v, l], i) => (
                <button key={v} onClick={() => setEstadoDoc(v)} title={ESTADOS_FATURA[v]?.descricao || ''} style={segBotao(estadoDoc === v, i > 0)}>{l}</button>
              ))}
            </div>
          </div>

          <div>
            <label style={rotulo}>Pesquisar</label>
            <input
              type="search"
              value={texto}
              onChange={e => setTexto(e.target.value)}
              placeholder="Nº do documento ou cliente"
              style={{ ...inputStyle, minWidth: '220px' }}
            />
          </div>
        </div>

        <div>
          <span style={{ fontSize: '13px', fontWeight: '500' }}>Estados das marcações:</span>
          <div style={{ display: 'inline-flex', gap: '14px', flexWrap: 'wrap', marginTop: '6px', marginLeft: '8px' }}>
            {estadosPermitidos.map(e => (
              <label key={e} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={!!estadoSel[e]}
                  onChange={() => setEstadoSel(p => ({ ...p, [e]: !p[e] }))}
                  style={{ width: '16px', height: '16px', accentColor: ESTADOS[e].cor }}
                />
                <span style={{ color: ESTADOS[e].cor, fontWeight: '600' }}>{ESTADOS[e].label}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {carregando ? <Loading /> : (
        <>
          {/* Resumo */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(158px, 1fr))', gap: '12px', marginBottom: '22px' }}>
            <Cartao titulo="Faturas emitidas" valor={resumo.nEmitidas} nota={`${resumo.nFS} FS · ${resumo.nFR} FR`} />
            <Cartao titulo="Total faturado" valor={fmtEur(resumo.faturado)} nota="no período filtrado" destaque />
            <Cartao titulo="Canceladas" valor={resumo.nCanceladas} nota={`${fmtEur(resumo.anulado)} retirados`} />
            <Cartao titulo="Linhas faturadas" valor={resumo.nLinhas} nota="parcelas nos documentos" />
          </div>

          <p style={{ fontWeight: '600', marginBottom: '10px', fontSize: '14px' }}>
            Faturas <span style={{ color: 'var(--cor-texto-suave)', fontWeight: '400', fontSize: '12px' }}>({filtradas.length})</span>
          </p>

          <div style={{ background: 'var(--cor-branco)', border: '1px solid var(--cor-borda)', borderRadius: 'var(--raio-lg)', overflow: 'hidden', boxShadow: 'var(--sombra)' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={tabela}>
                <thead>
                  <tr style={trHead}>
                    <th style={thL}>Documento</th>
                    <th style={thL}>Data</th>
                    <th style={thL}>Cliente</th>
                    <th style={thL}>Método</th>
                    <th style={thL}>Marcações</th>
                    <th style={thL}>Estado</th>
                    <th style={thR}>Total</th>
                    <th style={thR}></th>
                  </tr>
                </thead>
                <tbody>
                  {filtradas.length === 0 ? (
                    <tr>
                      <td colSpan={8} style={{ padding: '32px 16px', textAlign: 'center', color: 'var(--cor-texto-suave)', fontSize: '13.5px' }}>
                        {semMetodos
                          ? 'Nenhum meio de pagamento selecionado — escolhe pelo menos um.'
                          : 'Sem faturas para estes filtros.'}
                      </td>
                    </tr>
                  ) : paginaLinhas.map(f => {
                    const visiveis = itensVisiveis(f)
                    const estadosDoc = ORDEM_ESTADOS.filter(e => visiveis.some(i => i.estado === e))
                    return (
                      <tr
                        key={f.id}
                        style={{ ...trBody, cursor: 'pointer', opacity: f.estado === 'emitida' ? 1 : 0.62 }}
                        onClick={() => { setFaturaAberta(f); setAConfirmar(false) }}
                        title="Ver detalhe da fatura"
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--cor-creme)'}
                        onMouseLeave={e => e.currentTarget.style.background = ''}
                      >
                        <td style={tdL}>
                          <span style={{ fontWeight: '600', whiteSpace: 'nowrap' }}>{f.id}</span>
                          {f.tipo && <span style={tag}>{f.tipo}</span>}
                        </td>
                        <td style={{ ...tdL, whiteSpace: 'nowrap' }}>{f.data ? format(new Date(f.data), 'dd/MM/yyyy HH:mm') : '—'}</td>
                        <td style={tdL}>{f.cliente || '—'}</td>
                        <td style={tdL}>{labelMetodo(f.metodo, metodosTenant)}</td>
                        <td style={tdL}>
                          {contarMarcacoes(visiveis)}
                          {f.lote && <span style={{ color: 'var(--cor-texto-suave)', fontSize: '11.5px' }}> · lote</span>}
                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '3px' }}>
                            {estadosDoc.map(e => <PilulaEstado key={e} estado={e} />)}
                          </div>
                        </td>
                        <td style={tdL}>
                          <span
                            title={estadoFaturaCfg(f.estado).descricao}
                            style={{ color: estadoFaturaCfg(f.estado).cor, fontWeight: '600', fontSize: '12px' }}
                          >
                            ● {estadoFaturaCfg(f.estado).label}
                          </span>
                        </td>
                        <td style={{ ...tdR, fontWeight: '700', whiteSpace: 'nowrap' }}>{fmtEur(f.total)}</td>
                        <td style={tdR}>
                          <button
                            className="btn-secundario"
                            style={{ padding: '5px 11px', fontSize: '12.5px' }}
                            onClick={ev => { ev.stopPropagation(); setFaturaAberta(f); setAConfirmar(false) }}
                          >
                            Detalhe
                          </button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', padding: '11px 14px', background: 'var(--cor-creme)', borderTop: '1px solid var(--cor-borda)', fontSize: '12.5px', color: 'var(--cor-texto-suave)', flexWrap: 'wrap' }}>
              <span>
                {filtradas.length} documento{filtradas.length === 1 ? '' : 's'} · emitidos somam{' '}
                <strong style={{ color: 'var(--cor-texto)' }}>{fmtEur(resumo.faturado)}</strong>
              </span>
              {totalPaginas > 1 && (
                <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <button onClick={() => setPagina(p => Math.max(1, p - 1))} disabled={paginaAtual === 1} className="btn-secundario" style={{ ...btnMini, opacity: paginaAtual === 1 ? 0.5 : 1 }}>← Anterior</button>
                  <span>Página {paginaAtual} de {totalPaginas}</span>
                  <button onClick={() => setPagina(p => Math.min(totalPaginas, p + 1))} disabled={paginaAtual === totalPaginas} className="btn-secundario" style={{ ...btnMini, opacity: paginaAtual === totalPaginas ? 0.5 : 1 }}>Seguinte →</button>
                </span>
              )}
            </div>
          </div>
        </>
      )}

      {faturaAberta && (
        <PainelFatura
          fatura={faturaAberta}
          itens={itensVisiveis(faturaAberta)}
          metodosTenant={metodosTenant}
          podeCancelar={podeFazer('cancelar_fatura')}
          aConfirmar={aConfirmar}
          setAConfirmar={setAConfirmar}
          reabrirPagamentos={reabrirPagamentos}
          setReabrirPagamentos={setReabrirPagamentos}
          cancelando={cancelando}
          obtendoPdf={obtendoPdf}
          onFechar={fecharPainel}
          onPdf={() => obterPdf(faturaAberta.id)}
          onCancelar={cancelarFatura}
          onAbrirMarcacao={id => navigate(`/admin/agendamento/${id}`)}
          onAbrirCliente={id => navigate(`/admin/clientes/${id}`)}
        />
      )}
    </div>
  )
}

// ── Painel lateral de detalhe ────────────────────────────────
function PainelFatura({
  fatura, itens, metodosTenant, podeCancelar, aConfirmar, setAConfirmar,
  reabrirPagamentos, setReabrirPagamentos,
  cancelando, obtendoPdf, onFechar, onPdf, onCancelar, onAbrirMarcacao, onAbrirCliente,
}) {
  const nOcultas = (fatura.itens || []).length - itens.length
  const somaVisivel = itens.reduce((s, i) => s + i.valor, 0)
  const nMarc = new Set(itens.map(i => i.agendamento_id).filter(Boolean)).size
  // O cancelamento afeta o documento inteiro, incluindo o que este perfil não vê.
  const nTodas = new Set((fatura.itens || []).map(i => i.agendamento_id).filter(Boolean)).size

  return (
    <>
      <div onClick={onFechar} style={{ position: 'fixed', inset: 0, background: 'rgba(44,26,14,.34)', zIndex: 90 }} />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={`Fatura ${fatura.id}`}
        style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(520px, 100%)', background: 'var(--cor-branco)', borderLeft: '1px solid var(--cor-borda)', boxShadow: '-14px 0 40px rgba(44,26,14,.16)', zIndex: 91, display: 'flex', flexDirection: 'column' }}
      >
        <div style={{ padding: '18px 20px 14px', borderBottom: '1px solid var(--cor-borda)', background: 'var(--cor-creme)' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '19px', fontWeight: '600' }}>{fatura.id}</h3>
              <div style={{ marginTop: '6px', display: 'flex', gap: '7px', alignItems: 'center', flexWrap: 'wrap' }}>
                <span
                  title={estadoFaturaCfg(fatura.estado).descricao}
                  style={{ color: estadoFaturaCfg(fatura.estado).cor, fontWeight: '600', fontSize: '12px' }}
                >
                  ● {estadoFaturaCfg(fatura.estado).label}
                </span>
                {fatura.tipo && <span style={tag}>{TIPO_DOC[fatura.tipo] || fatura.tipo}</span>}
                {fatura.lote && <span style={tag}>Cobrança agrupada</span>}
              </div>
            </div>
            <button onClick={onFechar} aria-label="Fechar detalhe" style={{ border: 0, background: 'transparent', fontSize: '20px', lineHeight: 1, color: 'var(--cor-texto-suave)', cursor: 'pointer', padding: '2px 5px' }}>×</button>
          </div>

          <dl style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 16px', margin: '14px 0 0' }}>
            <div>
              <dt style={dtEstilo}>Cliente</dt>
              <dd style={ddEstilo}>
                {fatura.cliente_id
                  ? <button onClick={() => onAbrirCliente(fatura.cliente_id)} style={{ background: 'none', border: 0, padding: 0, color: 'var(--cor-principal)', fontWeight: '500', cursor: 'pointer', fontSize: '13.5px', fontFamily: 'inherit' }}>{fatura.cliente || '—'}</button>
                  : (fatura.cliente || '—')}
              </dd>
            </div>
            <div>
              <dt style={dtEstilo}>Emitida em</dt>
              <dd style={ddEstilo}>{fatura.data ? format(new Date(fatura.data), 'dd/MM/yyyy HH:mm') : '—'}</dd>
            </div>
            <div>
              <dt style={dtEstilo}>Método</dt>
              <dd style={ddEstilo}>{labelMetodo(fatura.metodo, metodosTenant)}</dd>
            </div>
            <div>
              <dt style={dtEstilo}>Total</dt>
              <dd style={{ ...ddEstilo, fontWeight: '700' }}>{fmtEur(fatura.total)}</dd>
            </div>
          </dl>
        </div>

        <div style={{ padding: '16px 20px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {/* Cancelada mas ainda válida no Vendus — é aqui que o operador
              percebe que tem trabalho por fazer lá. */}
          {fatura.estado === 'cancelada' && (
            <div style={{ background: '#FDF6E7', border: '1px solid #E4CDA0', borderRadius: 'var(--raio)', padding: '11px 13px', fontSize: '12.5px', lineHeight: 1.55, color: '#7A4E0B' }}>
              <strong>Cancelada no sistema</strong>
              {fatura.cancelada_em ? ` em ${format(new Date(fatura.cancelada_em), 'dd/MM/yyyy')}` : ''}
              {fatura.cancelada_por ? ` por ${fatura.cancelada_por}` : ''}.
              As parcelas continuam pagas e voltaram a ficar por faturar.{' '}
              <strong>O documento continua válido no Vendus</strong> — tem de ser lá anulado com nota de crédito.
            </div>
          )}

          {fatura.estado === 'anulada_vendus' && (
            <div style={{ background: '#FBEAEA', border: '1px solid #EFC9C9', borderRadius: 'var(--raio)', padding: '11px 13px', fontSize: '12.5px', lineHeight: 1.55, color: '#7F1D1D' }}>
              <strong>Anulada no Vendus</strong>
              {fatura.nota_credito_id ? ` (nota de crédito ${fatura.nota_credito_id})` : ''}
              {fatura.cancelada_por ? ` · cancelada por ${fatura.cancelada_por}` : ''}. Ciclo fechado.
            </div>
          )}

          <div>
            <p style={seccaoTit}>
              {nMarc} marcaç{nMarc === 1 ? 'ão' : 'ões'} · {itens.length} parcela{itens.length === 1 ? '' : 's'}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {itens.map((i, idx) => (
                <button
                  key={`${i.agendamento_id}-${i.parcela}-${idx}`}
                  onClick={() => i.agendamento_id && onAbrirMarcacao(i.agendamento_id)}
                  disabled={!i.agendamento_id}
                  style={{ display: 'block', width: '100%', textAlign: 'left', border: '1px solid var(--cor-borda)', borderRadius: 'var(--raio)', padding: '10px 12px', background: 'var(--cor-branco)', cursor: i.agendamento_id ? 'pointer' : 'default', fontFamily: 'inherit', color: 'inherit' }}
                  onMouseEnter={e => { if (i.agendamento_id) e.currentTarget.style.background = 'var(--cor-creme)' }}
                  onMouseLeave={e => e.currentTarget.style.background = 'var(--cor-branco)'}
                >
                  <span style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '10px' }}>
                    <span style={{ fontSize: '13.5px', fontWeight: '500' }}>{i.servico || '—'}</span>
                    <span style={{ fontSize: '13.5px', fontWeight: '600', whiteSpace: 'nowrap' }}>{fmtEur(i.valor)}</span>
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginTop: '4px', fontSize: '12px', color: 'var(--cor-texto-suave)' }}>
                    <span>{i.quando ? format(new Date(i.quando), 'dd/MM HH:mm') : '—'}</span>
                    <span>· {i.profissional || '—'}</span>
                    {i.estado && <PilulaEstado estado={i.estado} />}
                    <span style={tag}>{i.parcela === 'sinal' ? 'Sinal' : 'Restante'}</span>
                    {i.agendamento_id && (
                      <span style={{ marginLeft: 'auto', color: 'var(--cor-principal)', fontWeight: '600', fontSize: '11.5px' }}>Abrir marcação →</span>
                    )}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {nOcultas > 0 && (
            <p style={{ background: 'var(--cor-creme)', border: '1px dashed var(--cor-borda)', borderRadius: 'var(--raio)', padding: '9px 12px', fontSize: '12px', color: 'var(--cor-texto-suave)', lineHeight: 1.5, margin: 0 }}>
              {nOcultas} parcela{nOcultas === 1 ? '' : 's'} deste documento pertence{nOcultas === 1 ? '' : 'm'} a marcações
              em estados que este perfil não vê — por isso o total das linhas acima ({fmtEur(somaVisivel)}) não fecha
              com o total do documento.
            </p>
          )}
        </div>

        {aConfirmar ? (
          <div style={{ borderTop: '1px solid var(--cor-borda)', padding: '14px 20px', background: '#FBEAEA' }}>
            <p style={{ margin: '0 0 11px', fontSize: '12.5px', lineHeight: 1.55, color: '#7F1D1D' }}>
              Cancela a fatura <strong>{fatura.id}</strong> <strong>no sistema</strong>.{' '}
              {nTodas > 1
                ? <>O documento cobre <strong>{nTodas} marcações</strong> — todas ficam por faturar
                    {nOcultas > 0 ? `, incluindo ${nOcultas} que não aparece${nOcultas === 1 ? '' : 'm'} na lista acima` : ''}. </>
                : <>A marcação fica por faturar. </>}
              Anule também o documento no Vendus — isto não emite nota de crédito.
            </p>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '12.5px', color: '#7F1D1D', cursor: 'pointer', marginBottom: '11px', lineHeight: 1.5 }}>
              <input
                type="checkbox"
                checked={reabrirPagamentos}
                onChange={e => setReabrirPagamentos(e.target.checked)}
                style={{ width: '15px', height: '15px', marginTop: '2px', flex: 'none', accentColor: '#DC2626' }}
              />
              <span>
                Marcar {nTodas > 1 ? 'as marcações' : 'a marcação'} como <strong>não pagas</strong> — usa isto só se a cobrança em si foi um engano, não apenas o documento.
                Pagamentos por <strong>Stripe mantêm-se pagos</strong> de qualquer forma: já foram cobrados ao cliente, fora do nosso controlo.
              </span>
            </label>
            <div style={{ display: 'flex', gap: '9px', flexWrap: 'wrap' }}>
              <button onClick={onCancelar} disabled={cancelando} style={{ background: '#DC2626', color: '#fff', border: 'none', borderRadius: 'var(--raio)', padding: '7px 15px', fontSize: '12.5px', cursor: cancelando ? 'default' : 'pointer', opacity: cancelando ? 0.6 : 1, fontFamily: 'inherit' }}>
                {cancelando ? 'A cancelar…' : (reabrirPagamentos ? 'Confirmar cancelamento e reabrir' : 'Confirmar cancelamento')}
              </button>
              <button onClick={() => { setAConfirmar(false); setReabrirPagamentos(false) }} disabled={cancelando} className="btn-secundario" style={btnMini}>Voltar atrás</button>
            </div>
          </div>
        ) : (
          <div style={{ borderTop: '1px solid var(--cor-borda)', padding: '13px 20px', display: 'flex', gap: '9px', flexWrap: 'wrap', alignItems: 'center' }}>
            <button onClick={onPdf} disabled={obtendoPdf} className="btn-primario" style={{ padding: '7px 15px', fontSize: '13px', opacity: obtendoPdf ? 0.6 : 1 }}>
              {obtendoPdf ? 'A obter…' : 'Obter PDF'}
            </button>
            {fatura.estado === 'emitida' && podeCancelar && (
              <button
                onClick={() => setAConfirmar(true)}
                className="btn-secundario"
                style={{ ...btnMini, marginLeft: 'auto', color: '#B91C1C', borderColor: '#EFC9C9' }}
              >
                Cancelar fatura
              </button>
            )}
          </div>
        )}
      </aside>
    </>
  )
}

function PilulaEstado({ estado }) {
  const cfg = estadoCfg(estado)
  return (
    <span style={{ color: cfg.cor, fontWeight: '600', fontSize: '11px', whiteSpace: 'nowrap' }}>
      ● {cfg.label}
    </span>
  )
}

function Cartao({ titulo, valor, nota, destaque, alerta }) {
  const fundo = destaque ? 'var(--cor-principal)' : alerta ? '#FDF6E7' : 'var(--cor-branco)'
  const texto = destaque ? '#fff' : alerta ? '#7A4E0B' : 'var(--cor-texto)'
  return (
    <div style={{ background: fundo, color: texto, border: `1px solid ${alerta ? '#E4CDA0' : 'var(--cor-borda)'}`, borderRadius: 'var(--raio-lg)', padding: '15px 16px', boxShadow: 'var(--sombra)' }}>
      <div style={{ fontSize: '12px', opacity: 0.75, marginBottom: '5px' }}>{titulo}</div>
      <div style={{ fontSize: '21px', fontWeight: '700' }}>{valor}</div>
      {nota && <div style={{ fontSize: '11.5px', marginTop: '3px', opacity: destaque ? 0.75 : 1, color: destaque || alerta ? texto : 'var(--cor-texto-suave)' }}>{nota}</div>}
    </div>
  )
}

const inputStyle = { padding: '9px 12px', border: '1px solid var(--cor-borda)', borderRadius: 'var(--raio)', fontSize: '14px', fontFamily: 'var(--font-ui)', background: 'var(--cor-branco)', color: 'var(--cor-texto)' }
const rotulo = { display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500' }
const btnPreset = { padding: '7px 14px', fontSize: '13px' }
const btnMini = { padding: '6px 12px', fontSize: '12.5px' }
const segmentado = { display: 'inline-flex', border: '1px solid var(--cor-borda)', borderRadius: 'var(--raio)', overflow: 'hidden', background: 'var(--cor-branco)' }
const segBotao = (ativo, comBorda) => ({
  fontFamily: 'inherit', fontSize: '12.5px', padding: '9px 13px', border: 0,
  borderLeft: comBorda ? '1px solid var(--cor-borda)' : 0,
  background: ativo ? 'var(--cor-creme-escuro)' : 'transparent',
  color: ativo ? 'var(--cor-texto)' : 'var(--cor-texto-suave)',
  fontWeight: ativo ? '600' : '400', cursor: 'pointer',
})
const caixaCombo = { position: 'absolute', top: 'calc(100% + 5px)', left: 0, zIndex: 20, minWidth: '100%', width: 'max-content', background: 'var(--cor-branco)', border: '1px solid var(--cor-borda)', borderRadius: 'var(--raio)', boxShadow: '0 10px 28px rgba(44,26,14,.16)', padding: '6px', display: 'flex', flexDirection: 'column', gap: '1px' }
const opcaoCombo = { display: 'flex', alignItems: 'center', gap: '9px', padding: '7px 10px', borderRadius: '6px', fontSize: '13.5px', cursor: 'pointer', whiteSpace: 'nowrap' }
const tabela = { width: '100%', borderCollapse: 'collapse', fontSize: '13px' }
const trHead = { background: 'var(--cor-creme)', borderBottom: '1px solid var(--cor-borda)' }
const trBody = { borderBottom: '1px solid var(--cor-borda)' }
const thL = { padding: '10px 12px', textAlign: 'left', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--cor-texto-suave)', whiteSpace: 'nowrap' }
const thR = { ...thL, textAlign: 'right' }
const tdL = { padding: '9px 12px', textAlign: 'left' }
const tdR = { padding: '9px 12px', textAlign: 'right' }
const tag = { display: 'inline-block', marginLeft: '6px', padding: '2px 7px', borderRadius: '5px', fontSize: '10.5px', fontWeight: '600', background: 'var(--cor-creme-escuro)', color: 'var(--cor-texto-suave)' }
const dtEstilo = { fontSize: '10.5px', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--cor-texto-suave)', marginBottom: '2px' }
const ddEstilo = { margin: 0, fontSize: '13.5px' }
const seccaoTit = { fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--cor-texto-suave)', fontWeight: '600', margin: '0 0 8px' }
